import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Upload,
  Archive,
  Loader2,
  Users,
  RotateCcw,
  X,
  Download,
  Gamepad2,
  SlidersHorizontal,
  Package,
  Compass
} from 'lucide-react';
import { creatorsAPI } from '../api/creators';
import AddCreatorModal from '../components/AddCreatorModal';
import BulkCreatorUpload from '../components/BulkCreatorUpload';
import CreatorFiltersModal from '../components/CreatorFiltersModal';
import ImportsPanel from '../components/ImportsPanel';
import CreatorDiscovery from '../components/CreatorDiscovery';
import { getCountryFlag } from '../utils/formatters';
import { getFollowerLabel } from '../utils/platformHelpers';
import PlatformIcon from '../components/PlatformIcon';
import { useAuth } from '../context/AuthContext';

const Creators = () => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Bulk Upload is admin-only, plus Luke specifically (an analyst granted access).
  const canBulkUpload = isAdmin || user?.email?.toLowerCase() === 'luke@streamersconnected.com';
  const [showImports, setShowImports] = useState(false);
  const [allGenres, setAllGenres] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [archivingId, setArchivingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCreators, setArchivedCreators] = useState([]);
  const [totalCreators, setTotalCreators] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  // 'roster' = the creators on the platform (this page as it always was);
  // 'discover' = the pool of streamers/channels the scans have seen, on or off
  // the roster, searchable by game/language/country and addable in one click.
  const [mode, setMode] = useState('roster');
  const [filters, setFilters] = useState({
    // free-text quick search in the top bar (name / handle)
    search: '',
    // multi-value filters, stored as comma-separated strings for URL friendliness
    platform: '',
    country: '',
    genre: '',
    sizes: '',
    // single-value filters
    hasEmail: '',
    keywords: '',
    // game coverage
    gameId: '',
    gameName: '',      // UI-only, for the selected-game chip
    gameDays: '90',
    gameFrom: '',      // custom range (ISO date)
    gameTo: '',
  });

  // Build the query params to send to the API — strips UI-only fields and
  // drops the coverage window if no game is selected.
  const buildApiParams = () => {
    const params = { ...filters };
    delete params.gameName;
    if (!params.gameId) {
      delete params.gameDays;
      delete params.gameFrom;
      delete params.gameTo;
    }
    // If a custom date range is set, drop the preset day count so the
    // backend knows to use the custom bounds.
    if (params.gameFrom || params.gameTo) delete params.gameDays;
    Object.keys(params).forEach((k) => {
      if (params[k] === '' || params[k] === null || params[k] === undefined) delete params[k];
    });
    return params;
  };

  const fetchCreators = async () => {
    try {
      setLoading(true);
      const response = await creatorsAPI.getAll(buildApiParams());
      setCreators(response.data.creators);
      setTotalCreators(response.data.pagination?.total || response.data.creators.length);
    } catch (err) {
      setError('Failed to fetch creators');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const response = await creatorsAPI.exportCsv(buildApiParams());
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'creators.csv';
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
      setError('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return '1d ago';
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return months === 1 ? '1mo ago' : `${months}mo ago`;
  };

  const fetchGenres = async () => {
    try {
      const response = await creatorsAPI.getGenres();
      setAllGenres(response.data.genres || []);
    } catch (err) {
      console.error('Failed to fetch genres', err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await creatorsAPI.getCountries();
      setAllCountries(response.data.countries || []);
    } catch (err) {
      console.error('Failed to fetch countries', err);
    }
  };

  useEffect(() => {
    fetchGenres();
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchCreators();
  }, [filters]);

  const formatFollowers = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toString();
  };

  const getTierInfo = (followerCount) => {
    if (!followerCount) return { tier: 'Unknown', color: 'bg-gray-500', accent: '#6b7280' };
    if (followerCount >= 1000000) return { tier: 'Elite', color: 'bg-emerald-500', accent: '#10b981' };
    if (followerCount >= 500000) return { tier: 'Top Tier', color: 'bg-sc-pink', accent: '#ec4899' };
    if (followerCount >= 100000) return { tier: 'Rising', color: 'bg-purple-500', accent: '#8b5cf6' };
    if (followerCount >= 10000) return { tier: 'Growing', color: 'bg-amber-500', accent: '#f59e0b' };
    return { tier: 'Starter', color: 'bg-gray-400', accent: '#9ca3af' };
  };

  const getLinkedPlatforms = (creator) => {
    const platforms = [];
    if (creator.platform) platforms.push(creator.platform.toLowerCase());
    if (creator.youtubeUrl && !platforms.includes('youtube')) platforms.push('youtube');
    if (creator.twitchUrl && !platforms.includes('twitch')) platforms.push('twitch');
    if ((creator.kickUrl || creator.kickSlug) && !platforms.includes('kick')) platforms.push('kick');
    if (creator.tiktokUrl && !platforms.includes('tiktok')) platforms.push('tiktok');
    if (creator.twitterUrl) platforms.push('twitter');
    if (creator.instagramUrl) platforms.push('instagram');
    return [...new Set(platforms)];
  };

  const handleArchiveCreator = async (creatorId, creatorName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Archive ${creatorName}? They will be hidden and no longer scraped, but all data will be preserved.`)) return;
    setArchivingId(creatorId);
    try {
      await creatorsAPI.archive(creatorId);
      fetchCreators();
    } catch (err) {
      console.error('Failed to archive creator:', err);
      setError('Failed to archive creator.');
    } finally {
      setArchivingId(null);
    }
  };

  const fetchArchivedCreators = async () => {
    try {
      const response = await creatorsAPI.getArchived();
      setArchivedCreators(response.data.creators || []);
    } catch (err) {
      console.error('Failed to fetch archived creators:', err);
    }
  };

  const handleRestoreCreator = async (creatorId) => {
    try {
      await creatorsAPI.restore(creatorId);
      fetchArchivedCreators();
      fetchCreators();
    } catch (err) {
      console.error('Failed to restore creator:', err);
      setError('Failed to restore creator.');
    }
  };

  // Count the number of active filters (for the button badge).
  // Multi-value fields count per selection; free-text/single-value fields
  // count as 1 when non-empty.
  const countActive = () => {
    let n = 0;
    for (const key of ['platform', 'country', 'genre', 'sizes']) {
      if (filters[key]) n += String(filters[key]).split(',').filter(Boolean).length;
    }
    if (filters.keywords) n += 1;
    if (filters.hasEmail) n += 1;
    if (filters.gameId) n += 1;
    return n;
  };
  const activeFilterCount = countActive();
  const hasActiveFilters = Boolean(filters.search) || activeFilterCount > 0;

  // Tier legend data
  const tiers = [
    { label: 'Elite', desc: '1M+', color: 'bg-emerald-400' },
    { label: 'Top', desc: '500K+', color: 'bg-sc-pink' },
    { label: 'Rising', desc: '100K+', color: 'bg-purple-400' },
    { label: 'Growing', desc: '10K+', color: 'bg-amber-400' },
    { label: 'Starter', desc: '<10K', color: 'bg-gray-400' },
  ];

  if (loading && creators.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
        <div className="flex flex-col items-center">
          <Loader2 size={32} className="animate-spin text-sc-pink" />
          <span className="text-white mt-4 font-medium">Loading creators...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-5">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Creator Database</h1>
              {mode === 'roster' && (
                <p className="text-sm text-white">
                  {totalCreators} creators{hasActiveFilters ? ` (showing ${creators.length})` : ''}
                </p>
              )}
            </div>
            {/* Roster / Discover */}
            <div className="inline-flex rounded-xl bg-white/5 border border-white p-1">
              <button
                onClick={() => setMode('roster')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  mode === 'roster' ? 'bg-sc-pink/20 text-sc-pink' : 'text-white hover:bg-white/10'
                }`}
              >
                <Users className="w-4 h-4" />
                Roster
              </button>
              <button
                onClick={() => setMode('discover')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  mode === 'discover' ? 'bg-sc-pink/20 text-sc-pink' : 'text-white hover:bg-white/10'
                }`}
              >
                <Compass className="w-4 h-4" />
                Discover
              </button>
            </div>
          </div>
          {mode === 'roster' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              Add Creator
            </button>
            {canBulkUpload && (
              <button
                onClick={() => setShowBulkUpload(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white text-white hover:text-white hover:bg-white/10 transition-all text-sm font-medium"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload
              </button>
            )}
            <button
              onClick={() => setShowImports(!showImports)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-sm font-medium ${
                showImports
                  ? 'bg-sc-pink/15 border border-sc-pink/25 text-sc-pink'
                  : 'bg-white/5 border border-white text-white hover:text-white hover:bg-white/10'
              }`}
            >
              <Package className="w-4 h-4" />
              Imports
            </button>
            <button
              onClick={() => {
                setShowArchived(!showArchived);
                if (!showArchived) fetchArchivedCreators();
              }}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-sm font-medium ${
                showArchived
                  ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400'
                  : 'bg-white/5 border border-white text-white hover:text-white hover:bg-white/10'
              }`}
            >
              <Archive className="w-4 h-4" />
              Archived
            </button>
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              title="Export current list to CSV"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white text-white hover:text-white hover:bg-white/10 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
          </div>
          )}
        </div>

        {mode === 'discover' ? (
          <CreatorDiscovery onAdded={() => fetchCreators()} />
        ) : (
        <>
        {/* Tier legend + Filters */}
        <div className="bg-gray-900/60 border border-white rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Tier legend */}
            <div className="hidden lg:flex items-center gap-2.5 pr-4 border-r border-white/10">
              <span className="text-[11px] text-white font-medium">Tiers:</span>
              {tiers.map((t) => (
                <div key={t.label} className="flex items-center gap-1" title={t.desc}>
                  <span className={`w-2.5 h-2.5 rounded-full ${t.color}`} />
                  <span className="text-[11px] text-white">{t.label}</span>
                </div>
              ))}
            </div>

            {/* Quick search */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
              <input
                type="text"
                placeholder="Search creators..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white rounded-lg text-sm text-white placeholder-white/25 focus:outline-none focus:border-sc-pink/40 focus:bg-white/8 transition-all"
              />
            </div>

            {/* Filters button — opens the advanced filter modal */}
            <button
              onClick={() => setShowFiltersModal(true)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilterCount > 0
                  ? 'bg-sc-pink/15 border border-sc-pink/30 text-sc-pink'
                  : 'bg-white/5 border border-white text-white hover:text-white hover:bg-white/10'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold bg-sc-pink text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Inline chip: selected game (so you see it without opening the modal) */}
            {filters.gameId && (
              <div className="flex items-center gap-2 pl-3 pr-2 py-1.5 bg-sc-pink/10 border border-sc-pink/25 rounded-lg text-sm text-white">
                <Gamepad2 className="w-3.5 h-3.5 text-sc-pink" />
                <span className="truncate max-w-[180px] font-medium">{filters.gameName}</span>
                <button
                  onClick={() => setFilters({ ...filters, gameId: '', gameName: '', gameFrom: '', gameTo: '' })}
                  className="text-white hover:text-white p-0.5 rounded"
                  title="Clear game filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {hasActiveFilters && (
              <button
                onClick={() => setFilters({
                  search: '', platform: '', country: '', genre: '', sizes: '',
                  hasEmail: '', keywords: '',
                  gameId: '', gameName: '', gameDays: '90', gameFrom: '', gameTo: '',
                })}
                className="text-sc-pink hover:text-sc-pink/80 text-xs font-medium transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-red-400/60 hover:text-red-400"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Archived Creators */}
        {showArchived && (
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 mb-6">
            <h2 className="text-sm font-semibold text-amber-400 mb-3">Archived Creators</h2>
            {archivedCreators.length === 0 ? (
              <p className="text-sm text-amber-400/50">No archived creators</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {archivedCreators.map((creator) => (
                  <div key={creator.id} className="flex items-center gap-2 bg-white/5 border border-white px-3 py-2 rounded-lg text-sm">
                    <PlatformIcon platform={creator.platform} size="sm" />
                    <span className="font-medium text-white">{creator.name}</span>
                    <button
                      onClick={() => handleRestoreCreator(creator.id)}
                      className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 font-medium ml-2 text-xs"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && creators.length === 0 && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Users className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No creators found</h3>
              <p className="text-white mb-6 text-sm">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Add your first creator to get started'}
              </p>
              {!hasActiveFilters && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Add Creator
                </button>
              )}
            </div>
          </div>
        )}

        {/* Imports Panel — shows recent bulk-import jobs with progress + CSV download */}
        <ImportsPanel
          isOpen={showImports}
          onClose={() => setShowImports(false)}
        />

        {/* Creator Grid */}
        {creators.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {creators.map((creator) => {
              const tierInfo = getTierInfo(creator.followerCount);
              const linkedPlatforms = getLinkedPlatforms(creator);

              return (
                <div
                  key={creator.id}
                  onClick={() => navigate(`/dashboard/creators/${creator.id}`)}
                  className="bg-gray-900/80 border border-white rounded-xl cursor-pointer group transition-all duration-200 hover:border-white overflow-hidden relative"
                >
                  {/* Tier accent bar */}
                  <div className="h-1 w-full" style={{ background: tierInfo.accent }} />

                  {/* Subtle glow */}
                  <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-5 blur-2xl" style={{ background: tierInfo.accent }} />

                  <div className="p-4 relative z-10">
                    <div className="flex items-start gap-3">
                      {/* Profile image */}
                      <div className="relative flex-shrink-0">
                        {creator.profileImageUrl ? (
                          <img
                            src={creator.profileImageUrl}
                            alt={creator.name}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-sc-pink/30 transition-all"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-11 h-11 rounded-full bg-white/10 items-center justify-center text-base font-semibold text-white ring-2 ring-white/10 group-hover:ring-sc-pink/30 transition-all ${creator.profileImageUrl ? 'hidden' : 'flex'}`}
                        >
                          {creator.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      </div>

                      {/* Name & handle */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-sm truncate group-hover:text-sc-pink transition-colors">
                          {creator.name}
                        </h3>
                        <p className="text-xs text-white truncate">@{creator.handle}</p>
                      </div>

                      {/* Archive button */}
                      <button
                        onClick={(e) => handleArchiveCreator(creator.id, creator.name, e)}
                        disabled={archivingId === creator.id}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-white/10 text-white/20 hover:text-amber-400"
                        title="Archive"
                      >
                        {archivingId === creator.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Archive className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center gap-3 mt-3 text-xs">
                      <span className="font-semibold text-white">
                        {formatFollowers(creator.followerCount)}
                        <span className="font-normal text-white ml-1">{getFollowerLabel(creator.platform).toLowerCase()}</span>
                      </span>
                      {creator.avgViews > 0 && (
                        <span className="text-white">
                          {formatFollowers(creator.avgViews)} avg
                        </span>
                      )}
                      {creator.country && (
                        <span className="ml-auto" title={creator.country}>
                          {getCountryFlag(creator.country)}
                        </span>
                      )}
                    </div>

                    {/* Tier + platforms */}
                    <div className="flex items-center justify-between mt-3">
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
                        style={{
                          color: tierInfo.accent,
                          backgroundColor: `${tierInfo.accent}15`,
                          borderColor: `${tierInfo.accent}30`
                        }}
                      >
                        {tierInfo.tier}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {linkedPlatforms.map((platform) => (
                          <div key={platform} title={platform} className="opacity-60 group-hover:opacity-100 transition-opacity">
                            <PlatformIcon platform={platform} size="sm" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Genre tags */}
                    {creator.topGenres && creator.topGenres.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {creator.topGenres.slice(0, 3).map((genre, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-sc-pink/10 text-sc-pink/70 border border-sc-pink/15 font-medium"
                          >
                            {genre}
                          </span>
                        ))}
                        {creator.topGenres.length > 3 && (
                          <span className="text-[10px] text-white/20 self-center">
                            +{creator.topGenres.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Coverage strip — only when filtering by a game */}
                    {creator.coverage && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <div className="flex items-center gap-2 text-[11px] text-white mb-1">
                          <Gamepad2 className="w-3 h-3 text-sc-pink/60" />
                          <span className="truncate">{filters.gameName}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-white flex-wrap">
                          <span className="text-white font-medium">
                            {creator.coverage.contentCount} {creator.coverage.contentCount === 1 ? 'item' : 'items'}
                          </span>
                          {creator.coverage.totalViews > 0 && (
                            <>
                              <span className="text-white/20">•</span>
                              <span>{formatFollowers(creator.coverage.totalViews)} views</span>
                            </>
                          )}
                          {creator.coverage.peakCcv > 0 && (
                            <>
                              <span className="text-white/20">•</span>
                              <span>{formatFollowers(creator.coverage.peakCcv)} peak CCV</span>
                            </>
                          )}
                          {creator.coverage.lastCoverageAt && (
                            <>
                              <span className="text-white/20">•</span>
                              <span>{formatRelative(creator.coverage.lastCoverageAt)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Niche tags */}
                    {creator.niche && creator.niche.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {creator.niche.slice(0, 2).map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-cyan-500/10 text-cyan-400/70 border border-cyan-500/15 font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                        {creator.niche.length > 2 && (
                          <span className="text-[10px] text-white/20 self-center">
                            +{creator.niche.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>

      {/* Add Creator Modal */}
      <AddCreatorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchCreators();
          setShowAddModal(false);
        }}
      />

      {/* Bulk Upload Modal */}
      <BulkCreatorUpload
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onSuccess={() => {
          fetchCreators();
          setShowBulkUpload(false);
        }}
        asImportJob
      />

      {/* Advanced Filters Modal */}
      <CreatorFiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        filters={filters}
        setFilters={setFilters}
        allCountries={allCountries}
        allGenres={allGenres}
      />
    </div>
  );
};

export default Creators;
